import { useEffect, useMemo } from 'react'
import {
  ShopItemsList,
  ShopItemRow,
  ShopItemSpacer,
  ProductThumb,
  ProductThumbPlaceholder,
  ProductName,
  ProductQty,
  CheckIcon,
} from './ShopItemsCard.styled'
import ShopDeliveryCheck from '../../images/shopDeliveryCheck.svg'
import ShopDeliveryCheckSelected from '../../images/shopDeliveryCheckSelected.svg'
import {
  applyVisitItemsDelivered,
  getStudioPickupBoardAsync,
  getVisitItemsAsync,
  markItemDeliveredAsync,
} from '../../redux/shop'
import type { IVisitShopItem } from '../../redux/shop'
import { selectStudioInventory } from '../../redux/shop/selectors'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { mergeVisitShopItemsByProduct, type IMergedVisitShopItem } from '../../utils/mergeVisitShopItemsByProduct'
import { canDeliverShopQuantity } from '../../utils/shopInventory'

interface Props {
  visitId?: string
}

const onImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.onerror = null
  e.currentTarget.style.visibility = 'hidden'
}

function DeliveryCheck({ delivered }: { delivered: boolean }) {
  const src = delivered ? ShopDeliveryCheckSelected : ShopDeliveryCheck
  return <img src={src} width={24} height={24} alt="" aria-hidden="true" />
}

function undeliveredQuantity(item: IMergedVisitShopItem, lines: IVisitShopItem[]): number {
  return item.orderItemIds.reduce((sum, orderItemId) => {
    const line = lines.find((row) => row.orderItemId === orderItemId)
    if (!line || line.delivered) return sum
    return sum + line.quantity
  }, 0)
}

/** Delivery checklist — all orders merged; duplicate products show as one row (e.g. ×2). */
const ShopItemsCard = ({ visitId }: Props) => {
  const dispatch = useAppDispatch()
  const items = useAppSelector((state) => state.shop.visitItems)
  const visitItemsVisitId = useAppSelector((state) => state.shop.visitItemsVisitId)
  const studioInventory = useAppSelector(selectStudioInventory)
  const clinicId = useAppSelector((state) => state.studioAuth.studioDetails?.id || state.studio.clinic?.id)

  useEffect(() => {
    if (visitId) {
      dispatch(getVisitItemsAsync({ visitId }))
    }
  }, [visitId, dispatch])

  const visitItems = visitId && visitItemsVisitId === visitId ? items : []
  const mergedItems = useMemo(() => mergeVisitShopItemsByProduct(visitItems), [visitItems])

  // Keep showing items for this visit even when every line is delivered.
  if (!visitId || (mergedItems.length === 0 && visitItems.length === 0)) return null

  const onToggle = (item: IMergedVisitShopItem, allDelivered: boolean) => {
    if (!visitId) return

    const targetDelivered = !allDelivered
    const idsToUpdate = item.orderItemIds.filter((orderItemId) => {
      const line = visitItems.find((it) => it.orderItemId === orderItemId)
      return line && line.delivered !== targetDelivered
    })
    if (idsToUpdate.length === 0) return

    const inventoryQuantity = idsToUpdate.reduce((sum, orderItemId) => {
      const line = visitItems.find((it) => it.orderItemId === orderItemId)
      return sum + (line?.quantity ?? 0)
    }, 0)

    if (targetDelivered && !canDeliverShopQuantity(studioInventory, item.productId, inventoryQuantity)) {
      window.alert('This product is not in studio inventory.')
      return
    }

    dispatch(
      applyVisitItemsDelivered({
        orderItemIds: idsToUpdate,
        delivered: targetDelivered,
        productId: item.productId,
        inventoryQuantity,
      }),
    )

    void (async () => {
      try {
        await Promise.all(
          idsToUpdate.map((orderItemId) =>
            dispatch(markItemDeliveredAsync({ orderItemId, visitId, delivered: targetDelivered })).unwrap(),
          ),
        )
      } catch {
        dispatch(getVisitItemsAsync({ visitId }))
        if (clinicId) dispatch(getStudioPickupBoardAsync({ clinicId }))
        window.alert('This product is not in studio inventory.')
      }
    })()
  }

  return (
    <ShopItemsList>
      {mergedItems.map((it) => {
        const qtyNeeded = undeliveredQuantity(it, visitItems)
        const deliveryBlocked =
          !it.delivered && qtyNeeded > 0 && !canDeliverShopQuantity(studioInventory, it.productId, qtyNeeded)

        return (
          <ShopItemRow
            key={it.productId}
            type="button"
            role="checkbox"
            aria-checked={it.delivered}
            aria-disabled={deliveryBlocked}
            aria-label={`${it.name}, quantity ${it.quantity}. ${
              deliveryBlocked ? 'Not in studio inventory' : it.delivered ? 'Mark not delivered' : 'Mark delivered'
            }`}
            $blocked={deliveryBlocked}
            disabled={deliveryBlocked}
            onClick={() => {
              if (deliveryBlocked) return
              onToggle(it, it.delivered)
            }}
          >
            {it.imageUrl ? <ProductThumb src={it.imageUrl} alt="" onError={onImgError} /> : <ProductThumbPlaceholder />}
            <ProductName>{it.name}</ProductName>
            <ProductQty>{it.quantity}</ProductQty>
            <CheckIcon aria-hidden="true">
              <DeliveryCheck delivered={it.delivered} />
            </CheckIcon>
          </ShopItemRow>
        )
      })}
      {mergedItems.length % 2 === 1 && <ShopItemSpacer aria-hidden="true" />}
    </ShopItemsList>
  )
}

export default ShopItemsCard
